"use client";

import { Button } from "@/components/ui/button";
import { GlassStars } from "@/components/ui/glass-stars";
import { GoogleG } from "@/components/ui/google-g";
import { MethodSection } from "@/components/home/MethodSection";
import { useAvailability } from "@/hooks/useAvailability";
import { CallBookingAnimation } from "@/components/visuals/CallBookingAnimation";
import { AnimatePresence, m as motion, useInView } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  Layers,
  Clock,
  BarChart3,
  Gauge,
  Code2,
  Zap,
  Shield,
  Check,
  X,
  MessageCircle,
} from "lucide-react";
import { useRef, useState, type ReactNode } from "react";
import Link from "next/link";
import { trackEvent } from "@/utils/analytics";

type Lang = "it" | "en";

// Evidenzia in ciano le parole chiave dentro una recensione (solo enfasi visiva,
// testo invariato). Usato per la recensione principale in home.
function highlightPrimary(text: string, keywords: string[]): ReactNode {
  if (!keywords.length) return text;
  const escaped = keywords
    .slice()
    .sort((a, b) => b.length - a.length)
    .map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`(${escaped.join("|")})`, "g");
  return text.split(re).map((part, i) =>
    keywords.includes(part) ? (
      <span key={i} className="font-medium text-primary">
        {part}
      </span>
    ) : (
      part
    ),
  );
}
type ProjectPreview = {
  src: string;
  width: number;
  height: number;
};

const projectPreviews = {
  neurashield: {
    src: "/clients/projects/previews/neurashieldtechnology.com-preview.jpg",
    width: 1512,
    height: 850,
  },
  dimensione4: {
    src: "/clients/projects/previews/dimensione4.it-preview.jpg",
    width: 1512,
    height: 850,
  },
  vistafarm: {
    src: "/clients/projects/previews/vistafarm.it-preview.jpg",
    width: 1440,
    height: 625,
  },
  erastudio: {
    src: "/clients/projects/previews/erastudio.it-preview.jpg",
    width: 1440,
    height: 700,
  },
  goodog: {
    src: "/clients/projects/previews/goodog.it-preview.jpg",
    width: 1440,
    height: 872,
  },
  ecoristrutturiamo: {
    src: "/clients/projects/www.ecoristrutturiamo.it.png",
    width: 1899,
    height: 832,
  },
  imusicfun: {
    src: "/clients/projects/previews/www.imusicfun.it-preview.jpg",
    width: 1440,
    height: 645,
  },
  riempilozaino: {
    src: "/clients/projects/previews/riempilozaino.com-preview.jpg",
    width: 1440,
    height: 704,
  },
  labncs: {
    src: "/clients/projects/previews/lab-ncs.com-preview.jpg",
    width: 1440,
    height: 599,
  },
  dariobellini: {
    src: "/clients/projects/previews/dariobellini.it-preview.jpg",
    width: 1440,
    height: 691,
  },
} as const satisfies Record<string, ProjectPreview>;

function ConceptV2({ lang }: { lang: Lang }) {
  const isItalian = lang === "it";
  const ref = useRef<HTMLElement | null>(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const dimensions = [
    {
      icon: Gauge,
      title: isItalian
        ? "Un sito che non porta clienti"
        : "A site that brings no clients",
      description: isItalian
        ? "Hai un sito ma non genera contatti, oppure \u00E8 vecchio e lento."
        : "You have a site but it brings no leads, or it's old and slow.",
      highlight: isItalian
        ? "Lo rifaccio o lo sistemo perch\u00E9 torni a lavorare per te."
        : "I rebuild or fix it so it works for you again.",
    },
    {
      icon: Code2,
      title: isItalian
        ? "Un progetto no-code da finire"
        : "A no-code project to finish",
      description: isItalian
        ? "Hai costruito con Lovable o Bubble, ma non \u00E8 solido n\u00E9 sicuro."
        : "You built with Lovable or Bubble, but it's not solid or secure.",
      highlight: isItalian
        ? "Lo trasformo in un prodotto vero, affidabile e sicuro."
        : "I turn it into a real, reliable, secure product.",
    },
    {
      icon: Clock,
      title: isItalian ? "Troppo lavoro manuale" : "Too much manual work",
      description: isItalian
        ? "Ogni settimana perdi ore in attivit\u00E0 ripetitive."
        : "Every week you lose hours on repetitive tasks.",
      highlight: isItalian
        ? "Le automatizzo con l'AI, cos\u00EC recuperi tempo."
        : "I automate them with AI, so you get time back.",
    },
    {
      icon: Layers,
      title: isItalian
        ? "Un'idea ferma nel cassetto"
        : "An idea stuck in a drawer",
      description: isItalian
        ? "Hai un'idea da tempo ma non sai da dove partire."
        : "You've had an idea for a while but don't know where to start.",
      highlight: isItalian
        ? "La porto a un prototipo concreto da far provare a clienti veri."
        : "I bring it to a working prototype real clients can try.",
    },
  ];

  return (
    <section
      id="concept"
      ref={ref}
      data-snap-label={isItalian ? "Problemi" : "Problems"}
      className="section-padding relative overflow-hidden cv-auto"
    >
      <div className="container-wide relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-left mb-12 md:mb-16"
        >
          <span className="font-mono text-label text-primary mb-4 block">
            {isItalian ? "Ti riconosci?" : "Sound familiar?"}
          </span>
          <h2 className="font-display text-h2 font-bold mb-4">
            {isItalian
              ? "Forse sei in una di queste situazioni"
              : "You might be in one of these situations"}
          </h2>
          <div className="text-body-lg text-muted-foreground max-w-[54rem] xl:mx-0 space-y-2 text-left">
            {isItalian
              ? "Se uno di questi problemi è il tuo, sei nel posto giusto. Te lo risolvo e ti faccio crescere."
              : "If one of these problems is yours, you're in the right place. I solve it and help you grow."}
          </div>
        </motion.div>

        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6">
          {dimensions.map((dim, index) => (
            <motion.div
              key={dim.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 * (index + 1) }}
              className="group surface-card p-6 md:p-8 relative flex flex-col text-left"
            >
              <div className="relative mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 md:h-12 md:w-12">
                {/* Composite-only hover overlay: animates opacity (not background-color). */}
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 rounded-xl bg-primary/10 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                />
                <dim.icon className="relative h-5 w-5 text-primary md:h-6 md:w-6" />
              </div>
              <h3 className="mb-3 font-display text-xl font-semibold leading-snug md:text-2xl">
                {dim.title}
              </h3>
              <p className="text-body text-muted-foreground leading-relaxed">
                {dim.description}
              </p>
              <p className="text-gradient font-medium mt-5">{dim.highlight}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServicesV2({
  lang,
  routes,
}: {
  lang: Lang;
  routes: Record<string, string>;
}) {
  const isItalian = lang === "it";
  const ref = useRef<HTMLElement | null>(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const services = [
    {
      icon: Code2,
      title: isItalian
        ? "Da progetto no-code a prodotto vero"
        : "From no-code project to real product",
      description: isItalian
        ? "Hai costruito con Lovable, Bubble o v0? Lo rendo solido, sicuro e pronto a crescere."
        : "Built with Lovable, Bubble or v0? I make it solid, secure and ready to grow.",
      tags: isItalian
        ? ["No-code", "Sicurezza", "Rifacimento", "Scalabilit\u00E0"]
        : ["No-code", "Security", "Rebuild", "Scalability"],
    },
    {
      icon: Gauge,
      title: isItalian
        ? "Siti, ecommerce e app su misura"
        : "Custom sites, ecommerce and apps",
      description: isItalian
        ? "Costruisco o rifaccio siti, ecommerce e app veloci, fatti per portarti clienti."
        : "I build or rebuild fast sites, ecommerce and apps made to bring you clients.",
      tags: isItalian
        ? ["Siti", "Ecommerce", "App mobile", "Performance"]
        : ["Sites", "Ecommerce", "Mobile apps", "Performance"],
    },
    {
      icon: Zap,
      title: isItalian ? "Automazioni con l'AI" : "AI automations",
      description: isItalian
        ? "Tolgo il lavoro manuale ripetitivo: processi che girano da soli."
        : "I remove repetitive manual work: processes that run on their own.",
      tags: isItalian
        ? ["AI", "Automazione", "Integrazioni", "n8n"]
        : ["AI", "Automation", "Integrations", "n8n"],
    },
  ];

  return (
    <section
      id="services"
      ref={ref}
      className="section-padding bg-surface/30 cv-auto"
      data-snap-label={isItalian ? "Servizi" : "Services"}
    >
      <div className="container-wide">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="flex flex-col xl:flex-row xl:items-end justify-between gap-6 mb-12 text-left"
        >
          <div className="xl:mx-0">
            <span className="font-mono text-label text-primary mb-4 block">
              {isItalian ? "Cosa faccio" : "What I do"}
            </span>
            <h2 className="font-display text-h2 font-bold">
              {isItalian
                ? "Strumenti digitali per crescere"
                : "Digital tools for growth"}
            </h2>
          </div>
          <Button variant="outline" className="rotating-border shrink-0" asChild>
            <Link
              href={routes.services}
              onClick={() =>
                trackEvent("cta_click", {
                  cta_text: isItalian ? "Scopri i servizi" : "Explore services",
                  cta_section: "services",
                })
              }
            >
              {isItalian ? "Vedi tutti i servizi" : "View all services"}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </Button>
        </motion.div>

        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 * (index + 1) }}
              className="group surface-card p-6 md:p-8 relative text-left"
            >
              <div className="relative mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 md:h-12 md:w-12">
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 rounded-xl bg-primary/10 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                />
                <service.icon className="relative h-5 w-5 text-primary md:h-6 md:w-6" />
              </div>
              <h3 className="mb-2 font-display text-lg font-semibold md:text-xl">
                {service.title}
              </h3>
              <p className="mb-4 text-sm text-muted-foreground md:text-body">
                {service.description}
              </p>
              <div className="flex flex-wrap gap-2">
                {service.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2 py-1 rounded-md bg-surface/70 border border-[hsl(var(--border))] text-[11px] md:text-xs font-mono text-muted-foreground"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProofV2({ lang }: { lang: Lang }) {
  const isItalian = lang === "it";
  const ref = useRef<HTMLElement | null>(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const points = [
    {
      icon: Zap,
      title: isItalian
        ? "Pi\u00F9 richieste qualificate"
        : "More qualified requests",
      description: isItalian
        ? "Percorsi e CTA progettati per ridurre attrito e aumentare conversione."
        : "Flows and CTAs designed to reduce friction and increase conversion.",
    },
    {
      icon: Shield,
      title: isItalian
        ? "Migliore velocit\u00E0 e usabilit\u00E0"
        : "Better speed and usability",
      description: isItalian
        ? "Performance e UX curate in modo concreto, non solo estetico."
        : "Performance and UX handled concretely, not just visually.",
    },
    {
      icon: BarChart3,
      title: isItalian ? "Meno dispersione" : "Less dispersion",
      description: isItalian
        ? "Struttura e messaggio allineati agli obiettivi di business."
        : "Structure and message aligned with business goals.",
    },
  ];

  const included = isItalian
    ? [
        "Un messaggio chiaro su cosa fai",
        "Sito veloce (conta per i clienti e per Google)",
        "Google ti trova pi\u00F9 facilmente",
        "Contenuti organizzati con ordine",
        "Grafica coerente su tutto il sito",
        "Ti seguo fino a quando \u00E8 online",
      ]
    : [
        "Clear messaging about what you do",
        "A fast site (it matters for clients and Google)",
        "Google finds you more easily",
        "Well-organized content",
        "Consistent design across the site",
        "I stay with you until it's online",
      ];
  const notIncluded = isItalian
    ? [
        "Strategia di vendita",
        "Gestione dell'ecommerce",
        "Campagne pubblicitarie",
        "Gestione dei social",
      ]
    : [
        "Sales strategy",
        "E-commerce management",
        "Advertising campaigns",
        "Social media management",
      ];

  return (
    <section
      id="proof"
      ref={ref}
      className="section-padding bg-surface/30 cv-auto"
      data-snap-label={isItalian ? "Risultati" : "Results"}
    >
      <div className="container-wide">
        <div className="grid gap-8 xl:grid-cols-2 xl:gap-12 xl:items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="text-left"
          >
            <span className="font-mono text-label text-primary mb-4 block">
              {isItalian ? "Risultati" : "Results"}
            </span>
            <h2 className="font-display text-h2 font-bold mb-8 max-w-3xl xl:mx-0">
              {isItalian
                ? "Risultati concreti, non promesse generiche."
                : "Concrete outcomes, not generic promises."}
            </h2>
            <div className="space-y-6 max-w-2xl xl:mx-0">
              {points.map((proof, index) => (
                <motion.div
                  key={proof.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.4, delay: 0.1 * (index + 1) }}
                  className="flex items-start gap-4 text-left"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <proof.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-display font-semibold mb-1">
                      {proof.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {proof.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="surface-card p-6 md:p-7 xl:p-8 max-w-3xl w-full xl:max-w-[40rem] xl:ml-auto"
          >
            <p className="font-mono text-label text-primary mb-2 text-left">
              {isItalian ? "In ogni progetto" : "In every project"}
            </p>
            <h3 className="font-display text-2xl xl:text-[2rem] font-semibold mb-6 text-left">
              {isItalian
                ? "Cosa \u00E8 sempre incluso"
                : "What is always included"}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <p className="font-mono text-xs uppercase tracking-wide text-primary mb-3">
                  {isItalian ? "Sempre incluso" : "Always included"}
                </p>
                <ul className="space-y-2.5">
                  {included.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-2.5 text-sm leading-snug"
                    >
                      <span className="mt-0.5 w-5 h-5 shrink-0 rounded-full bg-primary/20 flex items-center justify-center shadow-[0_0_8px_hsl(var(--primary)/0.35)]">
                        <Check className="w-3 h-3 text-primary" />
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="sm:border-l sm:border-[hsl(var(--border))] sm:pl-6">
                <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground mb-3">
                  {isItalian ? "Che non faccio" : "What I don't do"}
                </p>
                <ul className="space-y-2.5">
                  {notIncluded.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-2.5 text-sm leading-snug text-muted-foreground"
                    >
                      <span className="mt-0.5 w-5 h-5 shrink-0 rounded-full bg-muted-foreground/10 flex items-center justify-center">
                        <X className="w-3 h-3 text-muted-foreground/70" />
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-4 text-xs text-muted-foreground/80 leading-snug">
                  {isItalian
                    ? "Se ti serve, ti indirizzo a chi lo fa bene."
                    : "If you need it, I'll point you to someone who does it well."}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function ReviewsV2({
  lang,
  routes,
}: {
  lang: Lang;
  routes: Record<string, string>;
}) {
  const isItalian = lang === "it";
  const ref = useRef<HTMLElement | null>(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const detailsLink = `${routes.contacts}#tidycal`;

  const tabs = [
    { id: "clients", label: isItalian ? "Clienti" : "Clients" },
    {
      id: "peers",
      label: isItalian ? "Colleghi & Manager" : "Peers & Managers",
    },
  ] as const;
  type TabId = (typeof tabs)[number]["id"];

  const [activeTab, setActiveTab] = useState<TabId>("clients");

  const clientTestimonials = [
    {
      rating: 5,
      workType_it: "Landing + app IoT",
      workType_en: "Landing + IoT app",
      stack: "Next.js + Flutter",
      year: "2026",
      text_it:
        "Ho lavorato con Dario per lo sviluppo di un'applicazione per telefono e della pipeline di comunicazione e storage dati, e non potevo chiedere di meglio. Competenza, disponibilità e professionalità. Super suggerito!",
      text_en:
        "I worked with Dario on a mobile application and the data communication and storage pipeline, and I could not have asked for better. Competence, availability and professionalism. Highly recommended!",
      signature: "Andrea Bracali",
      meta: "neurashieldtechnology.com",
      href: "https://www.neurashieldtechnology.com",
      group: "dev" as const,
      preview: projectPreviews.neurashield,
      previewMode: "cover" as const,
    },
    {
      rating: 5,
      workType_it: "Redesign & brand",
      workType_en: "Redesign & brand",
      stack: "Next.js",
      year: "2026",
      text_it:
        "Il sito che stai guardando: redesign completo, performance e struttura pensati per convertire.",
      text_en:
        "The site you are looking at: full redesign, performance and structure built to convert.",
      signature: "Dimensione 4",
      meta: "dimensione4.it",
      href: "/",
      group: "dev" as const,
      preview: projectPreviews.dimensione4,
      previewMode: "cover" as const,
    },
    {
      rating: 5,
      workType_it: "Ottimizzazione e-commerce",
      workType_en: "E-commerce optimization",
      stack: "WordPress + WooCommerce",
      year: "2023",
      text_it:
        "Ottimo professionista, ha risolto tutti i nostri problemi di ottimizzazione in breve tempo. Consigliato!",
      text_en:
        "Excellent professional. He solved all our optimization issues in a very short time. Highly recommended.",
      signature: "Alessio",
      meta: "vistafarm.it",
      href: "https://vistafarm.it",
      group: "wordpress" as const,
      preview: projectPreviews.vistafarm,
      previewMode: "cover" as const,
    },
    {
      rating: 5,
      workType_it: "Sito da zero",
      workType_en: "Website from scratch",
      stack: "Wix",
      year: "2023",
      text_it: "Molto professionale e puntuale. Lo consiglio assolutamente.",
      text_en: "Very professional and punctual. I absolutely recommend him.",
      signature: "Anna",
      meta: "erastudio.it",
      href: "https://erastudio.it",
      group: "wordpress" as const,
      preview: projectPreviews.erastudio,
      previewMode: "cover" as const,
    },
    {
      rating: 5,
      workType_it: "Web app custom",
      workType_en: "Custom web app",
      stack: "Laravel + Bootstrap + CSS",
      year: "2022",
      text_it:
        "Sono estremamente soddisfatto del lavoro svolto. Ha dimostrato grande competenza, tempestivit\u00E0 e attenzione ai dettagli. La comunicazione \u00E8 stata sempre chiara e il risultato finale ha superato le mie aspettative.",
      text_en:
        "I am extremely satisfied with the work delivered. He demonstrated great competence, responsiveness and attention to detail. Communication was always clear and the final result exceeded my expectations.",
      signature: "Lorenzo",
      meta: "goodog.it",
      href: "https://goodog.it",
      group: "dev" as const,
      preview: projectPreviews.goodog,
      previewMode: "contain" as const,
    },
    {
      rating: 5,
      workType_it: "Restyling sito",
      workType_en: "Website restyling",
      stack: "WordPress",
      year: "2024",
      text_it:
        "Dario \u00E8 un professionista che ha soddisfatto tutte le aspettative ma soprattutto andando oltre con creativit\u00E0 e supportando ogni richiesta.",
      text_en:
        "Dario is a professional who met all expectations and even went beyond with creativity and support for every request.",
      signature: "Massimiliano Spiga",
      meta: "ecoristrutturiamo.it",
      href: "https://www.ecoristrutturiamo.it",
      group: "wordpress" as const,
      preview: projectPreviews.ecoristrutturiamo,
      previewMode: "contain" as const,
    },
    {
      rating: 5,
      workType_it: "Supporto continuativo",
      workType_en: "Continuous support",
      stack: "WordPress",
      year: "2023",
      text_it:
        "Semplicemente perfetto. Preciso, puntuale e attento. Un ottimo professionista al quale ci affideremo anche in futuro.",
      text_en:
        "Simply perfect. Precise, punctual and attentive. A great professional we will rely on again in the future.",
      signature: "Simone Zani",
      meta: "imusicfun.it",
      href: "https://www.imusicfun.it",
      group: "wordpress" as const,
      preview: projectPreviews.imusicfun,
      previewMode: "contain" as const,
    },
    {
      rating: 5,
      workType_it: "Sito da zero",
      workType_en: "Website from scratch",
      stack: "WordPress",
      year: "2025",
      text_it:
        "Nuovo sito rilasciato con struttura pi\u00F9 chiara, performance migliori e base pronta per evoluzioni successive.",
      text_en:
        "New website delivered with clearer structure, better performance, and a solid base for future iterations.",
      signature: "Silvia Birollo",
      meta: "riempilozaino.com",
      href: "https://riempilozaino.com",
      group: "wordpress" as const,
      preview: projectPreviews.riempilozaino,
      previewMode: "cover" as const,
    },
    {
      rating: 5,
      workType_it: "Interventi evolutivi",
      workType_en: "Evolutionary updates",
      stack: "WordPress",
      year: "2023",
      text_it:
        "Interventi progressivi sul sito con miglioramenti UX, rifiniture tecniche e supporto operativo continuativo.",
      text_en:
        "Progressive website updates with UX improvements, technical refinements, and ongoing operational support.",
      signature: "Jasna",
      meta: "lab-ncs.com",
      href: "https://lab-ncs.com",
      group: "wordpress" as const,
      preview: projectPreviews.labncs,
      previewMode: "cover" as const,
    },
    {
      rating: 5,
      workType_it: "Portfolio personale",
      workType_en: "Personal portfolio",
      stack: "React + Three.js",
      year: "2021",
      text_it:
        "Portfolio sperimentale sviluppato per mostrare visual design, motion e componenti interattivi su stack moderno.",
      text_en:
        "Experimental portfolio built to showcase visual design, motion, and interactive components on a modern stack.",
      signature: "Dario Marco Bellini",
      meta: "dariobellini.it",
      href: "https://dariobellini.it",
      group: "dev" as const,
      preview: projectPreviews.dariobellini,
      previewMode: "contain" as const,
    },
  ];

  const googleReviews: {
    signature: string;
    avatar: string;
    imgPosition: string;
    site: string;
    siteUrl: string;
    text: string;
    paragraphs?: string[];
    keywords?: string[];
  }[] = [
    {
      signature: "Francesca Laezza",
      avatar: "/reviews/francesca-laezza.webp",
      imgPosition: "object-center",
      site: "paperdi.it",
      siteUrl: "https://www.paperdi.it",
      text: "Dario è davvero da 5 stelle. Una persona super disponibile, in gamba e professionale.",
    },
    {
      signature: "Andrea Bracali",
      avatar: "/reviews/andrea-bracali.webp",
      imgPosition: "object-[center_18%]",
      site: "neurashieldtechnology.com",
      siteUrl: "https://www.neurashieldtechnology.com",
      text: "Ho lavorato con Dario per lo sviluppo di un'applicazione per telefono e della pipeline di comunicazione e storage dati, e non potevo chiedere di meglio. Competenza, disponibilità e professionalità. Super suggerito!",
    },
    {
      signature: "Simone Zani",
      avatar: "/reviews/simone-zani.webp",
      imgPosition: "object-center",
      site: "imusicfun.it",
      siteUrl: "https://www.imusicfun.it",
      text: "Cinque stelle plus! Quello che mi ha colpito fin da subito è stata l'incredibile competenza di Dario: non si limita a eseguire un lavoro, ma comprende davvero ciò di cui hai bisogno, proponendo soluzioni moderne e funzionali e in linea con la richiesta. Sa esattamente dove mettere le mani. Ma la vera differenza, secondo me, è rappresentata dalla sua disponibilità e dal suo approccio. Dario è sempre presente, pronto ad ascoltare e a guidare anche scelte complicate. Mi sono sentito seguito e supportato in ogni fase del progetto, aspetto che mi ha dato una grande tranquillità. Inoltre, ha una marcia in più nel problem solving. Ogni volta che si è presentato un intoppo tecnico o una sfida più complessa, Dario non si è mai perso d'animo: ha analizzato la situazione e ha trovato la soluzione perfetta in tempi record, senza mai far pesare la difficoltà della cosa. Se cercate un professionista serio, preparato e su cui poter contare ciecamente, non posso che consigliare Dario. Un'esperienza impeccabile dall'inizio alla fine!",
      paragraphs: [
        "Cinque stelle plus! Quello che mi ha colpito fin da subito è stata l'incredibile competenza di Dario: non si limita a eseguire un lavoro, ma comprende davvero ciò di cui hai bisogno, proponendo soluzioni moderne e funzionali e in linea con la richiesta. Sa esattamente dove mettere le mani.",
        "Ma la vera differenza è la sua disponibilità e il suo approccio. Dario è sempre presente, pronto ad ascoltare e a guidare anche le scelte complicate. Mi sono sentito seguito e supportato in ogni fase del progetto.",
        "Inoltre ha una marcia in più nel problem solving: a ogni intoppo tecnico o sfida complessa ha analizzato la situazione e trovato la soluzione perfetta in tempi record, senza mai far pesare la difficoltà.",
        "Se cercate un professionista serio, preparato e su cui contare ciecamente, non posso che consigliare Dario. Un'esperienza impeccabile dall'inizio alla fine!",
      ],
      keywords: [
        "incredibile competenza",
        "comprende davvero ciò di cui hai bisogno",
        "soluzioni moderne e funzionali",
        "disponibilità",
        "seguito e supportato",
        "problem solving",
        "soluzione perfetta in tempi record",
        "professionista serio",
        "Un'esperienza impeccabile",
      ],
    },
  ];
  const googleProfileUrl = "https://share.google/rKTcFTSr9AXWY6Ff3";
  const addlanceUrl =
    "https://www.addlance.com/profili/dario-marco-bellini/web-designer/bariano/ODMzNDk=";

  // Fascia "Lavori recenti" (solo screenshot progetto, niente recensione ripetuta):
  // NeuraShield, riempilozaino, dimensione4 (redesign del sito, esempio di design).
  const featuredOrder = [
    "dimensione4.it",
    "neurashieldtechnology.com",
    "riempilozaino.com",
  ];
  const featuredClientCards = featuredOrder
    .map((meta) => clientTestimonials.find((item) => item.meta === meta))
    .filter((item): item is (typeof clientTestimonials)[number] => Boolean(item));

  const developerPeerTestimonials = [
    {
      signature: "Vincent - Software Developer",
      text_it:
        "Dario \u00E8 stato uno dei colleghi pi\u00F9 disponibili con cui ho lavorato. Le sue capacit\u00E0 comunicative sono molto forti, al punto da evitare incomprensioni nel lavoro quotidiano. Ha trasferito conoscenza sulle attivit\u00E0 del progetto, \u00E8 un ottimo ascoltatore ed \u00E8 un punto di riferimento quando si discute di miglioramenti di processo. Ha contribuito in modo concreto al team e ha mostrato attitudine da team player e potenziale leadership.",
      text_en:
        "Dario has been one of the approachable peers that I have been working with. His communication skills are very great to the point that no miscommunications are being experienced in our line of work. Dario has transferred knowledge for activities on this project. He is a great mentor and a great listener especially when some suggestion your around and improvement on the process. He has greatly contributed on our team which serves as a great example of team player and probably a great leader. Hope in future, we can collaborate more on this project or outside this project.",
    },
    {
      signature: "Luigi - Backend Developer",
      text_it:
        "Ho lavorato con Dario sul progetto Saipem ed \u00E8 stato davvero un piacere collaborare con lui. Come collega onshore ha contribuito molto alla crescita di conoscenza del team, dimostrando forti capacit\u00E0 di collaborazione e condivisione tecnica. Voglio esprimere apprezzamento per tutte le volte in cui ha aiutato me e il team a superare blocchi di conoscenza. La sua competenza tecnica \u00E8 stata fondamentale.",
      text_en:
        "I have worked with Dario on Project Saipem and it was a blast working with him! As an onshore colleague, he has contributed well on the knowledge of our growing team, and this shows his strong collaboration and teaching skills. I want to express my appreciation for all the times Dario has helped me and my team through times where there were knowledge blockings. Your technical expertise has been invaluable! Thank you for being so dependable.",
    },
    {
      signature: "Geneva - Software Developer",
      text_it:
        "Volevo prendermi un momento per ringraziarti per l'eccellente lavoro svolto. Hai mostrato grande impegno e disponibilit\u00E0 nel completare attivit\u00E0 assegnate, e il tuo contributo \u00E8 stato davvero prezioso per il team. \u00C8 stato un piacere collaborare con una persona affidabile e proattiva. Grazie per tutto il tuo duro lavoro e supporto.",
      text_en:
        "I wanted to take a moment to thank you for the excellent work you've done. You've shown great commitment and willingness in completing the tasks assigned to you, and your contribution has been truly valuable to our team. I'm very happy to collaborate with someone so reliable and proactive. Thanks again for all your hard work!",
    },
  ];

  const managerConsultantTestimonials = [
    {
      signature: "Alessandro - Project Manager",
      text_it:
        "Hai svolto un ottimo lavoro come parte del team di sviluppo nel progetto ePTW CIW per SAIPEM. Hai dimostrato crescita professionale e forte impegno nel completare le attivit\u00E0 nei tempi previsti e con qualit\u00E0. Negli ultimi mesi hai dimostrato grande proattivit\u00E0 nell'affrontare rapidamente problemi e criticit\u00E0.",
      text_en:
        "You did a great job as part of the development team in the ePTW CIW project for SAIPEM. You demonstrated professional growth and strong commitment in completing activities on time and with quality. In recent months, you showed great proactivity in quickly addressing problems and critical issues.",
    },
    {
      signature: "Consulting Manager - Enterprise Project",
      text_it:
        "Durante il progetto Dario ha dimostrato grande disponibilit\u00E0 nel supportare il team e contribuire alla risoluzione di problemi complessi.",
      text_en:
        "During the project Dario showed strong availability in supporting the team and helping resolve complex issues.",
    },
  ];

  const enterpriseProjectTestimonials = [
    {
      section_it: "Progetto Nexi / PagoPA",
      section_en: "Nexi / PagoPA Project",
      items: [
        {
          signature: "Senior Developer - Enterprise Payments Project",
          text_it:
            "Dario ha lavorato come Frontend Developer su applicazioni Angular integrate con servizi backend Java Spring Boot per sistemi di pagamento enterprise. Ha dimostrato ottime capacit\u00E0 di sviluppo frontend, attenzione alla qualit\u00E0 del codice e grande collaborazione con il team backend.",
          text_en:
            "Dario worked as a Frontend Developer on Angular applications integrated with Java Spring Boot backend services for enterprise payment systems. He demonstrated strong frontend development skills, attention to code quality and great collaboration with backend teams.",
        },
        {
          signature: "Technical Lead - Digital Payments Platform",
          text_it:
            "Dario ha contribuito allo sviluppo di componenti frontend Angular per piattaforme di pagamento integrate con servizi PagoPA. Ha dimostrato autonomia nello sviluppo, capacit\u00E0 di problem solving e ottima collaborazione con team distribuiti.",
          text_en:
            "Dario contributed to the development of Angular frontend components for payment platforms integrated with PagoPA services. He showed autonomy, problem-solving skills and great collaboration with distributed teams.",
        },
      ],
    },
    {
      section_it: "Progetto Digital Twin",
      section_en: "Digital Twin Project",
      items: [
        {
          signature: "3D Software Engineer - Digital Twin Platform",
          text_it:
            "Dario ha lavorato allo sviluppo di piattaforme Digital Twin utilizzando React, Three.js e sistemi di visualizzazione 3D di grandi dataset come Potree e WebODM. Ha dimostrato competenze nella gestione di scene 3D complesse e nell'integrazione con servizi cartografici come Bing Maps.",
          text_en:
            "Dario worked on Digital Twin platforms using React, Three.js and large 3D datasets visualization with Potree and WebODM. He demonstrated strong skills in handling complex 3D scenes and integrating mapping services such as Bing Maps.",
        },
        {
          signature: "Full Stack Developer - Geospatial Platform",
          text_it:
            "Dario ha contribuito allo sviluppo di piattaforme di visualizzazione geospaziale e modelli Digital Twin integrando tecnologie frontend e sistemi di gestione dati fotogrammetrici.",
          text_en:
            "Dario contributed to the development of geospatial visualization platforms and Digital Twin models integrating frontend technologies and photogrammetry data systems.",
        },
      ],
    },
    {
      section_it: "Progetto Vigili del Fuoco - AI Assistant",
      section_en: "Firefighters Project - AI Assistant",
      items: [
        {
          signature: "Engineering Manager - AI Platform",
          text_it:
            "Dario ha partecipato allo sviluppo di un assistente AI basato su microservizi Python containerizzati con Docker. Ha dimostrato ottime capacit\u00E0 nella progettazione di servizi backend scalabili e nell'integrazione di modelli AI in architetture distribuite.",
          text_en:
            "Dario contributed to the development of an AI assistant based on Python microservices containerized with Docker. He demonstrated strong skills in designing scalable backend services and integrating AI models into distributed architectures.",
        },
        {
          signature: "Software Developer - AI Systems",
          text_it:
            "Durante lo sviluppo dell'assistente AI Dario ha contribuito alla progettazione dell'architettura a microservizi e all'integrazione tra servizi backend e sistemi di orchestrazione container.",
          text_en:
            "During the development of the AI assistant Dario contributed to the microservices architecture design and integration between backend services and container orchestration systems.",
        },
      ],
    },
  ];

  const renderClientCard = (
    item: (typeof clientTestimonials)[number],
    index: number,
    delayOffset = 0
  ) => (
    <motion.article
      key={`${item.meta}-${item.signature}`}
      layout
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.25, delay: delayOffset + index * 0.05 }}
      data-snap-anchor
      className="snap-anchor flex h-full flex-col surface-card overflow-hidden border border-[hsl(var(--border))] transition-shadow duration-300 hover:shadow-[0_12px_30px_rgba(0,0,0,0.28)]"
    >
      <div className="relative aspect-[16/9] overflow-hidden border-b border-[hsl(var(--border))] bg-black/20">
        <img
          src={item.preview.src}
          alt={`${item.signature} project preview`}
          width={item.preview.width}
          height={item.preview.height}
          sizes="(min-width: 1280px) 44vw, (min-width: 768px) 46vw, 92vw"
          loading="lazy"
          decoding="async"
          className={`h-full w-full ${item.meta === "riempilozaino.com" ? "object-left" : "object-center"} ${item.previewMode === "contain" ? "object-contain" : "object-cover"}`}
          onError={(event) => {
            event.currentTarget.src = "/placeholder.svg";
            event.currentTarget.className =
              "w-full h-full object-cover object-center";
          }}
        />
      </div>

      <div className="flex flex-1 flex-col p-5">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-mono px-2.5 py-1 rounded-full bg-primary/10 text-primary border border-primary/30">
            {isItalian ? item.workType_it : item.workType_en}
          </span>
          <span className="text-[11px] font-mono px-2.5 py-1 rounded-full bg-surface text-muted-foreground border border-[hsl(var(--border))]">
            {item.stack}
          </span>
          <span className="text-[11px] font-mono px-2.5 py-1 rounded-full bg-surface text-muted-foreground border border-[hsl(var(--border))]">
            {item.year}
          </span>
        </div>

        {/* Card "lavoro" (no recensione ripetuta): solo screenshot + meta + link sito. */}
        <div className="mt-auto pt-1">
          {item.meta ? (
            <a
              href={item.href}
              {...(item.href.startsWith("http")
                ? { target: "_blank", rel: "noopener noreferrer" }
                : {})}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition-opacity hover:opacity-80"
            >
              {item.meta}
              <ArrowUpRight className="h-4 w-4" />
            </a>
          ) : null}
        </div>
      </div>
    </motion.article>
  );

  const peerQuotes = [
    ...developerPeerTestimonials.map((q) => ({
      text_it: q.text_it,
      text_en: q.text_en,
      signature: q.signature,
      context: "Accenture",
    })),
    ...managerConsultantTestimonials.map((q) => ({
      text_it: q.text_it,
      text_en: q.text_en,
      signature: q.signature,
      context: "Accenture",
    })),
    ...enterpriseProjectTestimonials.map((section) => ({
      text_it: section.items[0].text_it,
      text_en: section.items[0].text_en,
      signature: section.items[0].signature,
      context: isItalian ? section.section_it : section.section_en,
    })),
  ];

  const spotlightGoogle = googleReviews[2];
  const restGoogle = googleReviews.filter((_, i) => i !== 2);
  const spotlightPeer = peerQuotes[0];
  const restPeers = peerQuotes.slice(1);

  // Nome / ruolo dalla firma "Nome - Ruolo" + contesto (azienda o progetto).
  const peerName = (sig: string) => sig.split(" - ")[0].trim();
  const peerRole = (sig: string, ctx: string) =>
    [sig.split(" - ").slice(1).join(" - ").trim(), ctx].filter(Boolean).join(" · ");
  // Parole chiave evidenziate in ciano nei feedback (skill + impatto). Match per lingua.
  const peerKeywords = isItalian
    ? ["capacità comunicative", "team player", "leadership", "problem solving", "competenza tecnica", "collaborazione", "qualità del codice", "autonomia", "proattività", "microservizi", "Angular", "Three.js", "React", "Digital Twin", "punto di riferimento", "affidabile", "disponibilità", "scalabili"]
    : ["communication skills", "team player", "leader", "problem-solving", "technical expertise", "collaboration", "code quality", "autonomy", "proactivity", "microservices", "Angular", "Three.js", "React", "Digital Twin", "mentor", "reliable", "dependable", "scalable"];

  return (
    <section
      id="reviews"
      ref={ref}
      className="section-padding relative cv-auto"
      data-snap-label={isItalian ? "Recensioni" : "Testimonials"}
    >
      <div className="container-wide">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.45 }}
          className="mb-8 max-w-3xl"
          data-snap-anchor
        >
          <span className="font-mono text-label text-primary mb-3 block">
            {isItalian ? "Dicono di me" : "What they say"}
          </span>
          <h2 className="font-display text-h2 font-bold mb-3">
            {isItalian
              ? "Recensioni reali, verificabili"
              : "Real, verifiable reviews"}
          </h2>
          <p className="text-body text-muted-foreground">
            {isItalian
              ? "Da clienti veri e da chi ha lavorato con me su progetti importanti. Tutte verificabili."
              : "From real clients and people who worked with me on serious projects. All verifiable."}
          </p>
        </motion.div>

        <div className="mb-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div
            role="tablist"
            aria-label={isItalian ? "Tipologia recensioni" : "Testimonials type"}
            className="inline-flex rounded-full border border-[hsl(var(--border))] bg-surface/80 p-1 supports-[backdrop-filter]:backdrop-blur-xl"
          >
            {tabs.map((tab) => {
              const isSelected = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  id={`reviews-tab-${tab.id}`}
                  role="tab"
                  type="button"
                  aria-selected={isSelected}
                  aria-controls={`reviews-panel-${tab.id}`}
                  tabIndex={isSelected ? 0 : -1}
                  onClick={() => setActiveTab(tab.id)}
                  className={`relative inline-flex min-h-11 items-center whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    isSelected
                      ? "text-primary"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {isSelected && (
                    <motion.span
                      layoutId="reviews-toggle-pill"
                      className="absolute inset-0 rounded-full border border-primary/30 bg-primary/10"
                      transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
                    />
                  )}
                  <span className="relative z-10">{tab.label}</span>
                </button>
              );
            })}
          </div>
          <a
            href={googleProfileUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={isItalian ? "5.0 su Google, recensioni verificate" : "5.0 on Google, verified reviews"}
            className="group inline-flex items-center gap-3 rounded-xl border border-[hsl(var(--border))] bg-surface/60 px-3.5 py-2 transition-colors hover:border-primary/30 supports-[backdrop-filter]:backdrop-blur-xl"
          >
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-black/5">
              <GoogleG className="h-[18px] w-[18px]" />
            </span>
            <span className="flex flex-col leading-tight">
              <span className="flex items-center gap-1.5">
                <span className="text-sm font-bold text-foreground">5.0</span>
                <GlassStars count={5} starClassName="h-3.5 w-3.5" />
              </span>
              <span className="text-xs text-muted-foreground transition-colors group-hover:text-foreground">
                {isItalian ? "recensioni verificate" : "verified reviews"} ↗
              </span>
            </span>
          </a>
        </div>

        <AnimatePresence mode="wait">
          {activeTab === "clients" ? (
            <motion.div
              key="clients"
              id="reviews-panel-clients"
              role="tabpanel"
              aria-labelledby="reviews-tab-clients"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 12 }}
              transition={{ duration: 0.25 }}
            >
              {/* Composizione asimmetrica: la recensione lunga fa da "muro"
                  a sinistra, le due brevi diventano tessere verticali a destra.
                  Su mobile: tutto impilato. */}
              <div className="mb-6 grid gap-5 lg:grid-cols-12 lg:items-stretch">
                {/* Spotlight: recensione principale */}
                <article className="relative flex flex-col overflow-hidden surface-card border border-primary/20 p-6 shadow-[0_0_40px_hsl(var(--primary)/0.08)] md:p-8 lg:col-span-7">
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-primary/10 blur-3xl"
                  />
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute right-5 top-1 select-none font-display text-[4rem] leading-none text-primary/10 md:text-[7rem]"
                  >
                    &rdquo;
                  </span>
                  <div className="relative flex items-center gap-3">
                    <span className="relative h-12 w-12 shrink-0 overflow-hidden rounded-full bg-primary/15">
                      <img
                        src={spotlightGoogle.avatar}
                        alt={spotlightGoogle.signature}
                        loading="lazy"
                        className={`absolute inset-0 h-full w-full object-cover ${spotlightGoogle.imgPosition}`}
                      />
                    </span>
                    <div className="min-w-0">
                      <cite className="block text-base font-semibold not-italic text-foreground">
                        {spotlightGoogle.signature}
                      </cite>
                      <a
                        href={spotlightGoogle.siteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-xs text-primary hover:underline"
                      >
                        {spotlightGoogle.site} ↗
                      </a>
                    </div>
                    <span className="ml-auto shrink-0 self-start rounded-full border border-primary/25 bg-primary/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wide text-primary">
                      {isItalian ? "In evidenza" : "Featured"}
                    </span>
                  </div>
                  <div className="relative mt-4">
                    <GlassStars count={5} />
                  </div>
                  <blockquote className="relative mt-5 space-y-4 text-[15px] leading-[1.75] text-foreground/85">
                    {(spotlightGoogle.paragraphs ?? [spotlightGoogle.text]).map(
                      (paragraph, i, arr) => (
                        <p key={i}>
                          {i === 0 ? "“" : null}
                          {highlightPrimary(paragraph, spotlightGoogle.keywords ?? [])}
                          {i === arr.length - 1 ? "”" : null}
                        </p>
                      ),
                    )}
                  </blockquote>
                </article>

                {/* Due recensioni brevi: tessere verticali con accento ciano */}
                <div className="grid gap-5 lg:col-span-5">
                  {restGoogle.map((review) => (
                    <article
                      key={review.signature}
                      className="group relative flex flex-col justify-center overflow-hidden surface-card border border-[hsl(var(--border))] p-6 transition-colors duration-300 hover:border-primary/30 md:p-7"
                    >
                      <span
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-y-0 left-0 w-[3px] bg-gradient-to-b from-primary/70 to-transparent opacity-70 transition-opacity duration-300 group-hover:opacity-100"
                      />
                      <div className="flex items-center gap-3">
                        <span className="relative h-11 w-11 shrink-0 overflow-hidden rounded-full bg-primary/15">
                          <img
                            src={review.avatar}
                            alt={review.signature}
                            loading="lazy"
                            className={`absolute inset-0 h-full w-full object-cover ${review.imgPosition}`}
                          />
                        </span>
                        <div className="min-w-0">
                          <cite className="block truncate text-sm font-semibold not-italic text-foreground md:text-base">
                            {review.signature}
                          </cite>
                          <a
                            href={review.siteUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-mono text-xs text-primary hover:underline"
                          >
                            {review.site} ↗
                          </a>
                        </div>
                      </div>
                      <div className="mt-4">
                        <GlassStars count={5} />
                      </div>
                      <blockquote className="mt-4 text-[15px] leading-relaxed text-foreground/85 md:text-base">
                        {`“${review.text}”`}
                      </blockquote>
                    </article>
                  ))}
                </div>
              </div>

              <div className="mt-10">
                <div className="mb-5 flex items-center justify-between gap-3">
                  <h3 className="font-display text-2xl font-bold text-foreground md:text-[1.75rem]">
                    {isItalian ? "Lavori recenti" : "Recent work"}
                  </h3>
                  <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wide text-muted-foreground">
                    <span className="relative flex h-2 w-2" aria-hidden="true">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[hsl(var(--success)/0.65)]" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-[hsl(var(--success))]" />
                    </span>
                    {isItalian ? "Progetti dal vivo" : "Live projects"}
                  </span>
                </div>
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {featuredClientCards.map((item, i) => renderClientCard(item, i))}
                </div>
              </div>

              <div className="surface-card mt-8 max-w-2xl border border-[hsl(var(--border))] p-5 md:p-6">
                <a
                  href={addlanceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mb-4 inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-wide text-muted-foreground transition-colors hover:text-primary"
                >
                  {isItalian
                    ? "Punteggio recensioni AddLance"
                    : "AddLance review score"}{" "}
                  ↗
                </a>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {[
                    { label_it: "Totale", label_en: "Overall" },
                    { label_it: "Puntualità", label_en: "Punctuality" },
                    { label_it: "Budget", label_en: "Budget" },
                    { label_it: "Comunicazione", label_en: "Communication" },
                  ].map((m) => (
                    <div
                      key={m.label_it}
                      className="flex items-center gap-2.5 text-sm"
                    >
                      <GlassStars count={5} starClassName="h-3.5 w-3.5" />
                      <span className="text-muted-foreground">
                        {isItalian ? m.label_it : m.label_en}
                      </span>
                      <span className="ml-auto font-semibold text-primary">
                        5.0
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="peers"
              id="reviews-panel-peers"
              role="tabpanel"
              aria-labelledby="reviews-tab-peers"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 12 }}
              transition={{ duration: 0.25 }}
            >
              {/* Spotlight peer: stesso design della recensione principale clienti. */}
              <article className="relative mb-6 flex flex-col overflow-hidden surface-card border border-primary/20 p-6 shadow-[0_0_40px_hsl(var(--primary)/0.08)] md:p-8">
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute right-5 top-1 select-none font-display text-[4rem] leading-none text-primary/10 md:text-[7rem]"
                >
                  &rdquo;
                </span>
                <div className="relative flex items-center gap-3">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/15 font-display text-lg font-bold text-primary">
                    {peerName(spotlightPeer.signature).charAt(0)}
                  </span>
                  <div className="min-w-0">
                    <cite className="block text-base font-semibold not-italic text-foreground">
                      {peerName(spotlightPeer.signature)}
                    </cite>
                    <span className="font-mono text-xs text-primary">
                      {peerRole(spotlightPeer.signature, spotlightPeer.context)}
                    </span>
                  </div>
                  <span className="ml-auto shrink-0 self-start rounded-full border border-primary/25 bg-primary/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wide text-primary">
                    {isItalian ? "In evidenza" : "Featured"}
                  </span>
                </div>
                <blockquote className="relative mt-5 text-[15px] leading-[1.75] text-foreground/85">
                  {"“"}
                  {highlightPrimary(isItalian ? spotlightPeer.text_it : spotlightPeer.text_en, peerKeywords)}
                  {"”"}
                </blockquote>
              </article>

              <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
                {restPeers.map((q) => (
                  <article
                    key={`${q.signature}-${q.context}`}
                    className="group relative flex flex-col overflow-hidden surface-card border border-[hsl(var(--border))] p-6 transition-colors duration-300 hover:border-primary/30"
                  >
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-y-0 left-0 w-[3px] bg-gradient-to-b from-primary/70 to-transparent opacity-70 transition-opacity duration-300 group-hover:opacity-100"
                    />
                    <div className="flex items-center gap-3">
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/15 font-display text-base font-bold text-primary">
                        {peerName(q.signature).charAt(0)}
                      </span>
                      <div className="min-w-0">
                        <cite className="block truncate text-sm font-semibold not-italic text-foreground md:text-base">
                          {peerName(q.signature)}
                        </cite>
                        <span className="block truncate font-mono text-xs text-primary">
                          {peerRole(q.signature, q.context)}
                        </span>
                      </div>
                    </div>
                    <blockquote className="mt-4 text-sm leading-relaxed text-foreground/80">
                      {"“"}
                      {highlightPrimary(isItalian ? q.text_it : q.text_en, peerKeywords)}
                      {"”"}
                    </blockquote>
                  </article>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mt-10">
          <a
            href={detailsLink}
            onClick={() =>
              trackEvent("book_call_click", {
                tool: "tidycal",
                location: "home_reviews",
              })
            }
            className="inline-flex items-center gap-2 font-mono text-sm text-primary hover:underline"
          >
            {isItalian
              ? "Vuoi parlarne direttamente? Prenota una call"
              : "Want to talk it through? Book a call"}{" "}
            ↗
          </a>
        </div>
      </div>
    </section>
  );
}

function CtaV2({
  lang,
  routes,
}: {
  lang: Lang;
  routes: Record<string, string>;
}) {
  const isItalian = lang === "it";
  const bookingLink = `${routes.contacts}#tidycal`;
  const ref = useRef<HTMLElement | null>(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { availability, loading } = useAvailability({ defer: true });
  const isAvailable = availability.status === "available";

  return (
    <section
      id="cta"
      ref={ref}
      className="section-padding relative overflow-hidden cv-auto"
      data-snap-label="CTA"
    >
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6 }}
        className="container-wide relative"
      >
        <div className="surface-card relative overflow-hidden p-8 md:p-12 xl:p-16">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -right-10 -top-10 h-72 w-72 rounded-full bg-[radial-gradient(circle,hsl(var(--primary)/0.14),transparent_70%)] blur-2xl"
          />
          <div className="grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr]">
            <div className="text-left">
          {!loading && (
            <div className="mb-3 inline-flex items-center gap-2.5 rounded-full border border-[hsl(var(--border))] bg-background/60 px-4 py-1.5">
              <span className="relative flex h-2.5 w-2.5">
                {isAvailable && (
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[hsl(var(--success)/0.5)]" />
                )}
                <span
                  className={`relative inline-flex h-2.5 w-2.5 rounded-full ${
                    isAvailable
                      ? "bg-[hsl(var(--success))]"
                      : "bg-muted-foreground"
                  }`}
                />
              </span>
              <span className="font-mono text-xs text-muted-foreground">
                {isAvailable ? (
                  <>
                    {isItalian ? "Disponibile" : "Available"}
                    {availability.slots > 0 && (
                      <>
                        {" · "}
                        <span className="font-semibold text-primary">
                          {isItalian
                            ? `${availability.slots} slot questo mese`
                            : `${availability.slots} slots this month`}
                        </span>
                      </>
                    )}
                    {isItalian ? " · risposta in 24h" : " · reply within 24h"}
                  </>
                ) : isItalian ? (
                  "Agenda quasi piena · scrivimi comunque"
                ) : (
                  "Calendar almost full · reach out anyway"
                )}
              </span>
            </div>
          )}
          <h2 className="font-display text-2xl sm:text-3xl md:text-h2 font-bold mb-4 leading-tight text-left">
            {isItalian
              ? "Vediamo insieme il prossimo passo."
              : "Let's figure out your next step."}
          </h2>
          <p className="text-body text-muted-foreground mb-8 max-w-xl">
            {isItalian
              ? "Una call di 30 minuti: mi racconti dove sei e cosa ti serve. Ne esce una proposta chiara con cosa faccio, tempi e prezzo. Senza impegno."
              : "A 30-minute call: tell me where you are and what you need. You get a clear proposal with what I do, timing and price. No commitment."}
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Button variant="hero" size="xl" className="cta-glow w-full sm:w-auto" asChild>
              <Link
                href={bookingLink}
                prefetch={false}
                onClick={() =>
                  trackEvent("book_call_click", {
                    tool: "calendly",
                    location: "cta_section",
                  })
                }
              >
                <MessageCircle className="w-4 h-4" />
                {isItalian ? "Prenota una call" : "Book a call"}
              </Link>
            </Button>
            <Button variant="outline" size="xl" className="rotating-border w-full sm:w-auto" asChild>
              <Link
                href={routes.services}
                onClick={() =>
                  trackEvent("cta_click", {
                    cta_text: isItalian
                      ? "Scopri i servizi"
                      : "Explore services",
                    cta_section: "cta_section",
                  })
                }
              >
                {isItalian ? "Vedi i servizi" : "View services"}
              </Link>
            </Button>
          </div>
            </div>

            <div className="hidden w-full max-w-sm justify-self-end lg:block">
              <CallBookingAnimation isItalian={isItalian} />
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}

export function HomeSecondarySections({
  lang,
  routes,
}: {
  lang: Lang;
  routes: Record<string, string>;
}) {
  return (
    <>
      <ConceptV2 lang={lang} />
      <ServicesV2 lang={lang} routes={routes} />
      <MethodSection />
      <ProofV2 lang={lang} />
      <ReviewsV2 lang={lang} routes={routes} />
      <CtaV2 lang={lang} routes={routes} />
    </>
  );
}
